import { isAfter, fromUnixTime } from 'date-fns';
import { jwtDecode, JwtPayload } from 'jwt-decode';
import { createAsyncThunk } from '@reduxjs/toolkit';

import { RootState } from 'App/Store';
import { getUrlParam } from 'utils/urlUtils';

import dealersApi from 'modules/dealers/service';
import { getLoginToken } from 'modules/dealers/selectors';
import { ConnectionTypeLogin, SelfOfficeLoginMethod } from 'modules/dealers/types/ContextResponse';

import { formatPhoneNumber, getDefaultChannelToSendCode, getRetryExpiration } from 'modules/communication/utils';
import authApi from './service';
import { LoginParams } from './types/LoginRequest';
import { LoginResponse, SendMFACodeResponse } from './types/LoginResponse';
import { hasValidQrCode } from './selectors';
import { PhoneNumber } from 'modules/selfServices/types/SelfService';

type InitLoginResponse = SendMFACodeResponse | LoginResponse;

export const login = createAsyncThunk<InitLoginResponse, LoginParams>(
  'login/init',
  async ({
    registration, connectionTypes, phoneNumber, locationId, qrCode, type, codeInputDisabled, searchType, ...mfaParams
  }, { dispatch, rejectWithValue, getState }) => {
    const state = getState() as RootState;
    const token = getUrlParam('token');
    const contextToken = getLoginToken(state);

    try {
      const { exp } = jwtDecode<JwtPayload>(contextToken);
      const currentDate = new Date();
      const expirationDate = fromUnixTime(exp);
      const isTokenExpired = isAfter(currentDate, expirationDate);

      // When token expired because user didn't refresh, refetch context to get new exp token
      if (isTokenExpired) {
        await dispatch(dealersApi.endpoints.getContext.initiate(undefined, { forceRefetch: true })).unwrap();
      }

      // the login data can have a valid qrCode within its state but we can choose to send the
      // registration nonetheless (without a qrCode then).
      if (qrCode && type && hasValidQrCode(state) && !codeInputDisabled) {
        return await dispatch(authApi.endpoints.sendQrCode.initiate({ type, qrCode })).unwrap();
      }

      const isLoginMFA = connectionTypes?.includes(ConnectionTypeLogin.MFA);
      const hasValidSearchTypeForLogin = searchType && [SelfOfficeLoginMethod.PHONE_NUMBER, SelfOfficeLoginMethod.REGISTRATION].includes(searchType);
      const isBreakdownInit = isLoginMFA && !hasValidSearchTypeForLogin && Boolean(registration);

      if (isLoginMFA && (hasValidSearchTypeForLogin || isBreakdownInit)) {
        const computedPhoneNumber = formatPhoneNumber(phoneNumber);

        const connectionParams = hasValidSearchTypeForLogin
          ? {
            searchType,
            ...(registration ? { value: registration, phoneNumber: computedPhoneNumber } : { value: computedPhoneNumber }),
          }
          : { registration, phoneNumber: computedPhoneNumber };

        const data = await dispatch(authApi.endpoints.getMFAConnection.initiate({
          type,
          ...connectionParams,
          ...mfaParams,
        })).unwrap();

        const { customerType } = data || {};
        const channel = getDefaultChannelToSendCode(data);
        const retryExpiration = getRetryExpiration(data, channel);

        if (!retryExpiration || isAfter(new Date(), new Date(retryExpiration))) {
          const { connectionId, selfServiceId } = data;
          return await dispatch(authApi.endpoints.sendMFACode.initiate({
            channel,
            customerType,
            connectionId,
            selfServiceId,
          })).unwrap();
        }
        return undefined;
      }

      if (token && (phoneNumber || registration) ) {
        const payload = registration ?
          { value: registration, type:SelfOfficeLoginMethod.REGISTRATION } :
          { value: phoneNumber as Partial<PhoneNumber>, type:SelfOfficeLoginMethod.PHONE_NUMBER } ;

        return await dispatch(authApi.endpoints.login.initiate({
          ...payload,
          locationId,
        })).unwrap();

      }
    } catch (error) {
      return rejectWithValue(error);
    }
    return rejectWithValue(undefined);
  },
);
